import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Media from 'components/common/Media'
import Theme from 'components/common/Theme/Theme'
import Mixin from 'components/common/Mixin'
import Banner from 'components/common/Banner/Banner'
import { ReadMoreComplex } from 'components/common/ReadMore'
import Button from 'components/common/Button/Button'
import PhoneLink from 'components/common/PhoneLink'
import { CALL_STORE_FOR_PRICE_FIRST_TEXT, SELECT_STORE_FOR_PRICE_TEXT } from 'constant'

const List = styled.ul`
  margin: 25px 0;
  padding: 0;
`

const Item = styled.li`
  padding-left: 11px;
  margin-bottom: 5px;

  ::before {
    display: inline-block;
    width: 3px;
    height: 3px;
    margin-right: 11px;
    clear: both;
    vertical-align: middle;
    background-color: ${Theme.colors.dark_red};
    border-radius: 50%;
    content: '';
  }
`

const Description = styled.div`
  line-height: 22px;
`

const createInputMap = (description, features, benefits) => [
  {
    Wrapper: Description,
    data: description
  },
  {
    Wrapper: List,
    ChildWrapper: Item,
    data: features,
    attrs: { 'data-at-tireline-features-list': true }
  },
  {
    Wrapper: Description,
    data: benefits,
    attrs: { 'data-at-tireline-benefits-list': true }
  }
]

const Details = ({
  benefits,
  description,
  features = [],
  productPrice = '',
  hasActiveVehicle,
  isShowSelectStore,
  isShowPrice,
  isShowCallStoreForPrice,
  showStoreLocatorModal,
  formattedStorePhoneNumber,
  storePhoneNumber,
  brandPromisePlan,
  isNoStoreShowPrice,
  itemMinPrice,
  itemMaxPrice
}) =>
  <React.Fragment>
    <ReadMoreComplex
      limit={ 370 }
      inputMap={ createInputMap(description, features, benefits) } />
    {
      brandPromisePlan && <div dangerouslySetInnerHTML={{ __html: brandPromisePlan }} />
    }
    <React.Fragment>
      {
        isNoStoreShowPrice && (itemMinPrice !== itemMaxPrice) &&
        <PriceBlock data-at-tireline-price-range isNoStoreShowPrice>
          <span data-at-tireline-price-range-label>Price range: </span>
          <Price data-at-tireline-price-range-value>${`${itemMinPrice}-$${itemMaxPrice}`}</Price>/each
        </PriceBlock>
      }
      {
        isNoStoreShowPrice && (itemMinPrice === itemMaxPrice) &&
        <PriceBlock data-at-tireline-price-range isNoStoreShowPrice>
          <span data-at-tireline-price-range-label>Price: </span>
          <Price data-at-tireline-price-range-value>${itemMinPrice}</Price>/each
        </PriceBlock>
      }
      {
        isShowSelectStore &&
        <SelectStore
          link
          data-at-globalsearch-product-price
          bolder
          alignTextStart
          onClick={ showStoreLocatorModal }>
          {SELECT_STORE_FOR_PRICE_TEXT}
        </SelectStore>
      }
      {
        isShowPrice && (itemMinPrice !== itemMaxPrice) &&
        <PriceBlock data-at-tireline-price-range>
          <span data-at-tireline-price-range-label>Price range: </span>
          <Price data-at-tireline-price-range-value>${`${itemMinPrice}-$${itemMaxPrice}`}</Price>/each
        </PriceBlock>
      }
      {
        isShowPrice && (itemMinPrice === itemMaxPrice) &&
        <PriceBlock data-at-tireline-price-range>
          <span data-at-tireline-price-range-label>Price: </span>
          <Price data-at-tireline-price-range-value>${itemMinPrice}</Price>/each
        </PriceBlock>
      }
      {
        isShowCallStoreForPrice &&
        <PhoneBlock data-at-tire-line-product-price>
          <Phone>{ CALL_STORE_FOR_PRICE_FIRST_TEXT }</Phone>
          <Media.Desktop><Phone>{ formattedStorePhoneNumber }</Phone></Media.Desktop>
          <Media.Mobile><StyledPhoneLink number={ storePhoneNumber } /></Media.Mobile>
        </PhoneBlock>
      }
      { !hasActiveVehicle &&
      <Banner data-at-compatibility-banner asset="check-circle" success>
        Add a vehicle to confirm tire compatibility.
      </Banner>
      }
    </React.Fragment>
  </React.Fragment>

Details.propTypes = {
  benefits: PropTypes.arrayOf(PropTypes.string),
  brandPromisePlan: PropTypes.string,
  description: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string),
  formattedStorePhoneNumber: PropTypes.string,
  hasActiveVehicle: PropTypes.bool,
  isNoStoreShowPrice: PropTypes.bool,
  isShowCallStoreForPrice: PropTypes.bool,
  isShowPrice: PropTypes.bool,
  isShowSelectStore: PropTypes.bool,
  itemMaxPrice: PropTypes.number,
  itemMinPrice: PropTypes.number,
  productPrice: PropTypes.string,
  showStoreLocatorModal: PropTypes.func,
  storePhoneNumber: PropTypes.string
}

export default Details

const PriceBlock = styled.div`
  ${Mixin.responsive('padding', '15px 0 30px', '15px 0 25px')}
  ${props => props.isNoStoreShowPrice && css`
    ${Mixin.responsive('padding', '15px 0 0', '15px 0 0')}
  `}
`

const Price = styled.span`
  margin: 0 8px 0 5px;
  line-height: 38px;
  color: ${Theme.colors.sub_grey};
  ${Mixin.font.GothamBold(28)}
  ${props => props.small && css`
    margin-right: 5px;
    line-height: 22px;
    color: ${Theme.colors.semidark_grey};
    ${Mixin.font.GothamBold(14)}
  `}
`

const SelectStore = styled(Button)`
  && {
    ${({ alignTextStart }) => alignTextStart && css`
      text-align: start;
    `}
    padding: 0;
    margin: 20px 0;
    display: block;
    ${Mixin.font.GothamBold(20)}
    ${Media.mobile`
      ${Mixin.font.GothamBold(16)}
    `}

    &:hover {
      text-decoration: underline;
    }
  }
`

const PhoneBlock = styled.div`
  margin: 20px 0;
`

const Phone = styled.div`
  ${Mixin.font.GothamBold(20)}
  ${Media.mobile`
    max-width: 77px;
    ${Mixin.font.GothamBold(16)}
  `}
`

const StyledPhoneLink = styled(PhoneLink)`
  display: block;
  ${Mixin.font.GothamBold(16)}
`
